// Drag-and-drop transition rules.
//
// Mirrors the prototype's `app.jsx:onColDrop` allow-list:
//
//   backlog → implementing      (start a queued ticket)
//   pr → done                   (mark merged)
//   * → done                    (cancel/escalate)
//
// Anything else is silently rejected — the prototype uses this to discourage
// nonsensical transitions (e.g. backlog → review).
//
// Backend reality check: the platform does not currently expose endpoints for
// most of these state transitions. The only one with a real backend action
// is "→ done" (which we wire to POST /runs/:id/cancel). The other transitions
// are client-only optimistic UI for v1; calling code is responsible for
// noting via `serverAction(...)` what backend call (if any) corresponds to
// each transition and surfacing TODOs to the user.

import type { ColumnId } from './types';

export function isAllowedTransition(from: ColumnId, to: ColumnId): boolean {
  if (from === to) return false;
  if (from === 'backlog' && to === 'implementing') return true;
  if (from === 'pr' && to === 'done') return true;
  if (to === 'done') return true;
  return false;
}

export type ServerAction = 'cancel' | 'start' | 'mark-merged' | null;

/**
 * What backend call (if any) corresponds to a given transition. Used by the
 * UI to decide whether to fire a real API call or just show a "coming soon"
 * toast for client-only optimistic moves.
 */
export function serverAction(from: ColumnId, to: ColumnId): ServerAction {
  if (!isAllowedTransition(from, to)) return null;
  // pr → done means "mark merged" — distinct from a cancel.
  // TODO: backend support for marking a PR-stage run as merged
  if (from === 'pr' && to === 'done') return 'mark-merged';
  // any other → done means cancel/escalate. POST /runs/:id/cancel handles it.
  if (to === 'done') return 'cancel';
  // TODO: backend support for moving a queued run into 'running' (start)
  if (from === 'backlog' && to === 'implementing') return 'start';
  return null;
}
